import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import { allPlans } from "@/lib/plans";
import { faqs } from "@/data/content";

const pageMeta = {
  "/": { title: null, description: null },
  "/meals": {
    title: "Our Meals — Breakfasts and Lunches for Children",
    description:
      "Every meal we make in one place: ten traditional Indian breakfasts and fifteen lunches on a three-week rotation, with ingredients, allergens and approximate nutrition.",
  },
  "/plans": {
    title: "Weekly and Monthly Kids Meal Plans",
    description:
      "Weekly and monthly children’s lunch subscriptions in Pune with age-based portions for ages 2–4, 5–7, 8–10 and 11+.",
  },
  "/how-it-works": {
    title: "How Our Kids Lunch Subscription Works",
    description:
      "Create a child profile, choose a weekly or monthly plan, confirm home or school delivery in Pune and receive fresh lunches.",
  },
  "/food-standards": {
    title: "Our Food Standards",
    description:
      "How we plan children’s meals, what we publish about ingredients and nutrition, and the claims we will not make until they are proven.",
  },
  "/our-story": {
    title: "Our Story",
    description:
      "Mothers by Mothers began with one mother’s experience of feeding her son well within the realities of modern family life.",
  },
  "/mothers-supporting-mothers": {
    title: "Mothers Supporting Mothers",
    description:
      "Our plan to create dependable, skilled work for trained mothers and women cooks as our kitchen network grows.",
  },
  "/faqs": {
    title: "FAQs",
    description:
      "Answers about age groups, allergies, portions, delivery in Pune, school delivery, storage, reheating, pausing and cancelling.",
  },
  "/contact": { title: "Contact Us", description: "Speak to our team about plans, allergies, delivery areas or school delivery in Pune." },
  "/get-started": {
    title: "Start Your Child’s Meal Plan",
    description: "Set up a weekly or monthly breakfast or lunch plan for your child, with home or school delivery across selected Pune areas.",
  },
  "/delivery-areas": {
    title: "Delivery and Service Areas in Pune",
    description: "Check whether we deliver children’s lunches to your Pune pincode or school.",
  },
  "/sign-in": { title: "Customer Sign In", description: "Sign in to manage your child profiles and lunch subscription." },
  "/account": { title: "My Account", description: "Manage child profiles, dietary information and your lunch subscription." },
  "/checkout": { title: "Checkout", description: "Set up your child’s lunch subscription." },
};

// Manages document title, meta description, canonical, robots and JSON-LD.
export const SeoHead = () => {
  const brand = useBrand();
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = pageMeta[pathname] || {};
    document.title = meta.title
      ? brand.seo.titleTemplate.replace("%s", meta.title)
      : brand.seo.defaultTitle;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => v && el.setAttribute(k, v));
      return el;
    };

    const description = meta.description || brand.seo.description;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="keywords"]', { name: "keywords", content: brand.seo.keywords.join(", ") });
    setMeta('meta[name="robots"]', {
      name: "robots",
      content: brand.seo.indexable ? "index, follow" : "noindex, nofollow",
    });
    setMeta('link[rel="canonical"]', {
      rel: "canonical",
      href: `${brand.seo.canonicalBase || window.location.origin}${pathname}`,
    });
    const siteUrl = (brand.seo.canonicalBase || window.location.origin).replace(/\/$/, "");
    const socialImage = brand.seo.socialImage?.startsWith("http")
      ? brand.seo.socialImage
      : `${siteUrl}${brand.seo.socialImage || "/brand/og-share.png"}`;

    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: brand.name });
    setMeta('meta[property="og:title"]', { property: "og:title", content: document.title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:url"]', { property: "og:url", content: `${siteUrl}${pathname}` });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_IN" });
    setMeta('meta[property="og:image"]', { property: "og:image", content: socialImage });
    setMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: socialImage });
    setMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    setMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    setMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: `${brand.name} — ${brand.descriptor}`,
    });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: document.title });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: socialImage });

    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["LocalBusiness", "FoodEstablishment"],
          name: brand.name,
          description: brand.descriptor,
          email: brand.contact.email,
          telephone: brand.contact.phone,
          servesCuisine: "Indian",
          areaServed: brand.serviceAreas.cities.map((c) => ({ "@type": "City", name: c })),
          address: { "@type": "PostalAddress", addressLocality: "Pune", addressRegion: "Maharashtra", addressCountry: "IN" },
        },
        ...allPlans().map((p) => ({
          "@type": "Product",
          name: `${brand.name} — ${p.name}`,
          description: `${p.summary} ${p.totalMeals} meals.`,
          brand: { "@type": "Brand", name: brand.name },
        })),        {
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    };
    let script = document.getElementById("mbm-structured-data");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "mbm-structured-data";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
  }, [brand, pathname]);

  return null;
};

export default SeoHead;
